import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const PartnerPasswordReset: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [hasValidToken, setHasValidToken] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  // Check for recovery token on mount
  useEffect(() => {
    const checkRecoveryToken = async () => {
      try {
        // Check for recovery type in URL hash (Supabase password reset flow)
        const hash = window.location.hash;
        
        if (!hash || !hash.includes('type=recovery')) {
          setError('Invalid or missing password reset link. Please request a new password reset.');
          setIsLoading(false);
          return;
        }

        const hashParams = new URLSearchParams(hash.substring(1));
        const type = hashParams.get('type');
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        
        if (type !== 'recovery' || !accessToken) {
          setError('Invalid password reset link. Please request a new password reset.');
          setIsLoading(false);
          return;
        }

        // 🔥 CRITICAL FIX: Establish session with the recovery token
        // This is what was missing - we need an active session to update password
        const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken || ''
        });

        if (sessionError || !sessionData.session) {
          console.error('Session establishment error:', sessionError);
          setError('Failed to verify password reset link. Please request a new one.');
          setIsLoading(false);
          return;
        }

        // Token is valid and session is established - allow password reset
        setHasValidToken(true);
        setIsLoading(false);

        // Clean URL without affecting the active session
        window.history.replaceState(null, '', window.location.pathname);

        // Extract and store user email for display
        if (sessionData.user?.email) {
          setUserEmail(sessionData.user.email);
        }
      } catch (err) {
        console.error('Error checking recovery token:', err);
        setError('Failed to verify password reset link. Please try again.');
        setIsLoading(false);
      }
    };

    checkRecoveryToken();
  }, []);

  const handleSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate passwords
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsUpdatingPassword(true);
    setError(null);

    try {
      // Update password using Supabase Auth
      const { data, error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) {
        setError(`Failed to update password: ${updateError.message}`);
        setIsUpdatingPassword(false);
        return;
      }

      if (!data.user) {
        setError('Failed to update password. Please request a new password reset link.');
        setIsUpdatingPassword(false);
        return;
      }

      // 🎯 CRITICAL FIX: Clear account lockout after successful password reset
      const { error: clearLockoutError } = await supabase
        .rpc('clear_partner_lockout', {
          reset_email: data.user.email?.toLowerCase() || ''
        });

      if (clearLockoutError) {
        console.warn('Failed to clear account lockout:', clearLockoutError);
        // Don't fail the operation - password was still updated successfully
      }

      // Verify partner account exists
      const { data: partnerData, error: partnerError } = await supabase
        .rpc('get_partner_by_email', { login_email: data.user.email?.toLowerCase() || '' });

      if (partnerError || !partnerData || partnerData.length === 0) {
        setError('Password updated successfully, but failed to verify partner account. Please try logging in.');
        setIsUpdatingPassword(false);
        return;
      }

      // Password updated successfully - sign out and redirect
      setSuccess('Password reset complete! Redirecting to login...');
      
      // 🔥 CRITICAL FIX: Sign out the user completely before redirect
      // This ensures no stale session state interferes with login
      setTimeout(async () => {
        try {
          await supabase.auth.signOut();
          
          // Navigate to login with success message
          navigate('/partner-login', { 
            state: { 
              message: 'Password reset successful! You can now log in with your new password.',
              email: data.user.email 
            },
            replace: true
          });
        } catch (signOutError) {
          console.error('Sign out error:', signOutError);
          // Navigate anyway even if sign out fails
          navigate('/partner-login', { 
            state: { 
              message: 'Password reset successful! Please log in with your new password.',
              email: data.user.email 
            },
            replace: true
          });
        }
      }, 2000);

    } catch (err) {
      console.error('Error updating password:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!hasValidToken && !isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <Icon name="AlertCircle" size={32} className="text-red-600" />
            </div>
            <h1 className="text-3xl font-bold text-foreground">Invalid Reset Link</h1>
            <p className="text-muted-foreground mt-2">
              {error || 'The password reset link is invalid or has expired.'}
            </p>
          </div>

          <div className="mt-8 space-y-4">
            <Link to="/partner-login">
              <Button className="w-full" iconName="ArrowLeft" iconPosition="left">
                Back to Login
              </Button>
            </Link>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-blue-900 font-medium mb-2">Need a new reset link?</p>
                  <p className="text-xs text-blue-800 mb-3">
                    If your link expired or isn't working, you can request a new password reset from the login page.
                  </p>
                  <Link to="/partner-login">
                    <Button variant="outline" size="sm" iconName="KeyRound" iconPosition="left">
                      Request New Reset Link
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Valid token - show password reset form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Icon name="Lock" size={32} className="text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-foreground">Set New Password</h1>
          <p className="text-muted-foreground mt-2">
            Choose a strong password for your TravelHeroes partner account
          </p>
          {userEmail && (
            <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center justify-center gap-2">
                <Icon name="Mail" size={16} className="text-purple-600" />
                <p className="text-sm text-purple-900 font-medium">
                  Resetting password for:
                </p>
              </div>
              <p className="text-sm text-purple-700 font-semibold mt-1">
                {userEmail}
              </p>
            </div>
          )}
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSetNewPassword}>
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-start gap-3">
              <Icon name="CheckCircle" size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium">Success!</p>
                <p className="text-xs mt-1">{success}</p>
              </div>
            </div>
          )}

          {error && !success && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium">Error</p>
                <p className="text-xs mt-1">{error}</p>
              </div>
              <button
                type="button"
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          )}

          <div>
            <label htmlFor="new-password" className="block text-sm font-medium text-foreground mb-2">
              New Password
            </label>
            <div className="relative">
              <Input
                id="new-password"
                name="new-password"
                type={showNewPassword ? "text" : "password"}
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 8 characters)"
                disabled={isUpdatingPassword || success !== null}
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                disabled={isUpdatingPassword || success !== null}
              >
                <Icon name={showNewPassword ? "EyeOff" : "Eye"} size={20} />
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="confirm-password" className="block text-sm font-medium text-foreground mb-2">
              Confirm New Password
            </label>
            <div className="relative">
              <Input
                id="confirm-password"
                name="confirm-password"
                type={showConfirmPassword ? "text" : "password"}
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                disabled={isUpdatingPassword || success !== null}
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                disabled={isUpdatingPassword || success !== null}
              >
                <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} size={20} />
              </button>
            </div>
          </div>

          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs text-blue-900 font-medium mb-1">Password Requirements</p>
                <ul className="text-xs text-blue-800 space-y-1">
                  <li>• Minimum 8 characters long</li>
                  <li>• Passwords must match</li>
                  <li>• Use a strong, unique password</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Button
              type="submit"
              className="w-full"
              disabled={isUpdatingPassword || success !== null}
              iconName={isUpdatingPassword || success ? "Loader2" : "Check"}
              iconPosition="left"
            >
              {success ? 'Redirecting...' : isUpdatingPassword ? 'Updating Password...' : 'Set New Password'}
            </Button>

            <Link to="/partner-login" className="block">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={isUpdatingPassword || success !== null}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Back to Login
              </Button>
            </Link>
          </div>
        </form>

        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <Icon name="Shield" size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-amber-900 font-medium mb-1">Security Tips</p>
              <ul className="text-xs text-amber-800 space-y-1">
                <li>• Don't reuse passwords from other sites</li>
                <li>• Use a combination of letters, numbers, and symbols</li>
                <li>• Store your password securely</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerPasswordReset;